import { FunctionComponent, RefObject } from "react";
import { useTranslation } from "react-i18next";

import { CollapseGrabber } from "@/components/CollapseGrabber/CollapseGrabber";
import { Spinner } from "@/components/Spinner/Spinner";
import { classNames } from "@/utils/classNames";
import { listSelector } from "@/pages/Private/redux/list/list.slice";
import { useAppSelector } from "@/redux/hooks";

import { ListPerson } from "../../../List/schema/list";
import { Tabs } from "../../../List/components/CampaignOverview/components/Lead/Lead";
import { ReviewLeadCompact } from "../../components/LeadCardCompact/ReviewLeadCompact";
import { ListScope } from "../../components/ListScopeFilter/listScope";
import { MessagesEmptyColumn } from "../../components/MessagesEmptyColumn/MessagesEmptyColumn";
import { MessagesLoadError } from "../../components/MessagesLoadError/MessagesLoadError";

import type { CustomListsAutocompleteForSequencerItem } from "../../../List/components/CustomListsAutocompleteForSequencer";

interface ReadyToSendLeadsColumnProps {
	rows: ListPerson[];
	showLeads: boolean;
	onToggleShowLeads: () => void;
	handleSetCurrentLead: (index: number) => void;
	handleTabsChange: (tab: Tabs) => void;
	onAssignClick: (row: ListPerson) => void;
	onIgnoreClick: (row: ListPerson) => void;
	isFetching: boolean;
	/** Whether another page can still be loaded (drives the sentinel spinner). */
	hasMorePages: boolean;
	/** Sentinel observed by the infinite-scroll effect in the page component. */
	leadsEndRef: RefObject<HTMLDivElement>;
	/** The applied list filter — shown by the empty state. */
	selectedLists: CustomListsAutocompleteForSequencerItem[];
	/** Whether the user is searching — picks the empty-state copy. */
	hasSearchQuery: boolean;
}

/**
 * The collapsible Review-to-send leads column: the lead cards, the empty state, and the
 * infinite-scroll sentinel. Extracted from `ReadyToSend.tsx`; mirrors `InboxLeadsColumn`
 * and `OutboxLeadsColumn`.
 */
export const ReadyToSendLeadsColumn: FunctionComponent<ReadyToSendLeadsColumnProps> = ({
	rows,
	showLeads,
	onToggleShowLeads,
	handleSetCurrentLead,
	handleTabsChange,
	onAssignClick,
	onIgnoreClick,
	isFetching,
	hasMorePages,
	leadsEndRef,
	selectedLists,
	hasSearchQuery,
}) => {
	const { t } = useTranslation();
	const { inboxLoadFailed } = useAppSelector(listSelector);

	return (
		<div
			className={`bg-white w-[467px] min-w-[467px] transition-all z-15 duration-300 h-full border-r pl-0 border-border-light pb-0 mb-4 relative ${
				showLeads ? "translate-x-0" : "translate-x-[0] !w-[35px] !min-w-[35px]"
			}`}
		>
			<CollapseGrabber
				ariaLabel={showLeads ? t("basics.collapse") : t("basics.expand")}
				collapsed={!showLeads}
				edge="right"
				onToggle={onToggleShowLeads}
			/>
			<div className={classNames("overflow-x-hidden h-full flex flex-col")}>
				<div
					className="flex flex-col max-h-full overflow-y-auto border-t border-border-light scrollbar-hide w-full"
					id="leads-container"
				>
					{rows.map((row: ListPerson, index) => (
						<ReviewLeadCompact
							key={`${row.id}-${row.prospect?.id}-${index}`}
							handleSetCurrentLead={handleSetCurrentLead}
							handleTabsChange={handleTabsChange}
							isCollapsed={!showLeads}
							row={row}
							onAssignClick={() => onAssignClick(row)}
							onIgnoreClick={() => onIgnoreClick(row)}
						/>
					))}

					{showLeads && !isFetching && !inboxLoadFailed && rows.length === 0 && (
						<MessagesEmptyColumn
							hasSearchQuery={hasSearchQuery}
							scope={ListScope.READY_TO_SEND}
							selectedLists={selectedLists}
						/>
					)}

					{showLeads && <MessagesLoadError />}

					{!isFetching && !inboxLoadFailed && hasMorePages ? (
						<div ref={leadsEndRef} className="flex items-center justify-center">
							<Spinner color="#4d66f5" />
						</div>
					) : isFetching ? (
						<div className="flex items-center justify-center">
							<Spinner color="#4d66f5" />
						</div>
					) : (
						<></>
					)}
				</div>
			</div>
		</div>
	);
};
